import {prefersReducedMotion} from "./progress"

const EMAILS = [
  {
    from: "no-reply@acme.example",
    subject: "Reset your password",
    preview: "Use this link to choose a new password.",
    answer: "transactional",
    why: "A password reset is a message the user asked for."
  },
  {
    from: "orders@acme.example",
    subject: "Your order from Acme",
    preview: "Receipt for order 1842, placed today.",
    answer: "transactional",
    why: "An order receipt is a record of a purchase the user made."
  },
  {
    from: "security@acme.example",
    subject: "Your sign-in code",
    preview: "482913 is your two-factor code.",
    answer: "transactional",
    why: "A 2FA code is a one-time reply to a sign-in the user started."
  },
  {
    from: "notify@acme.example",
    subject: "Ana commented on your doc",
    preview: "Ana left a comment on Q3 plan.",
    answer: "notification",
    why: "A comment ping is a product notification, not a campaign."
  },
  {
    from: "digest@acme.example",
    subject: "Your weekly summary",
    preview: "12 updates from the last seven days.",
    answer: "notification",
    why: "A weekly summary is a digest of activity, not a promo."
  },
  {
    from: "news@acme.example",
    subject: "October product newsletter",
    preview: "What shipped this month at Acme.",
    answer: "bulk",
    why: "A product newsletter is bulk mail to a list."
  },
  {
    from: "hello@acme.example",
    subject: "We miss you, 20% off",
    preview: "Come back this week and take 20% off.",
    answer: "bulk",
    why: "A win-back discount is marketing, even when it names the user."
  },
  {
    from: "orders@acme.example",
    subject: "Your order from Acme",
    preview: "Receipt for order 1843, plus 20% off your next order.",
    answer: "bulk",
    why: "A receipt with a big promo banner reads as marketing to filters."
  }
]

const OFFSET = {
  transactional: {x: -48, y: -20},
  notification: {x: 0, y: -36},
  bulk: {x: 48, y: -20}
}

function verdictFor(score, total) {
  if (score === total) return "You can sort a stream by what the mail is for."
  if (score >= 6) return "Most of these land in the right bucket."
  if (score >= 4) return "Mixed content is the one that trips people."
  return "Sort by what the mail is for, not how it looks."
}

function setBusy(root, busy) {
  root.querySelectorAll("[data-learn-sort]").forEach((button) => {
    button.disabled = busy
  })
}

function paintCard(root, email, animate) {
  const card = root.querySelector("[data-learn-card]")
  const from = root.querySelector("[data-learn-from]")
  const subject = root.querySelector("[data-learn-subject]")
  const preview = root.querySelector("[data-learn-preview]")
  if (from) from.textContent = email.from
  if (subject) subject.textContent = email.subject
  if (preview) preview.textContent = email.preview
  if (!card) return
  card.style.transform = ""
  card.style.opacity = "1"
  if (!animate || prefersReducedMotion()) return
  card.style.transition = "none"
  card.style.transform = "translateY(12px)"
  card.style.opacity = "0"
  window.requestAnimationFrame(() => {
    card.style.transition = "transform 280ms ease-out, opacity 280ms ease-out"
    card.style.transform = "translateY(0)"
    card.style.opacity = "1"
  })
}

function exitCard(card, bucket, done) {
  if (!card || prefersReducedMotion()) {
    done()
    return
  }
  const offset = OFFSET[bucket] || OFFSET.notification
  card.style.transition = "transform 320ms ease-out, opacity 320ms ease-out"
  card.style.transform = `translate(${offset.x}px, ${offset.y}px)`
  card.style.opacity = "0"
  window.setTimeout(done, 320)
}

function stateOf(root) {
  return {
    index: Number(root.dataset.learnIndex || 0),
    score: Number(root.dataset.learnScore || 0),
    counts: {
      transactional: Number(root.dataset.learnTransactional || 0),
      notification: Number(root.dataset.learnNotification || 0),
      bulk: Number(root.dataset.learnBulk || 0)
    }
  }
}

function writeState(root, state) {
  root.dataset.learnIndex = String(state.index)
  root.dataset.learnScore = String(state.score)
  root.dataset.learnTransactional = String(state.counts.transactional)
  root.dataset.learnNotification = String(state.counts.notification)
  root.dataset.learnBulk = String(state.counts.bulk)
  Object.entries(state.counts).forEach(([key, value]) => {
    const count = root.querySelector(`[data-learn-bucket='${key}'] [data-learn-bucket-count]`)
    if (count) count.textContent = String(value)
  })
  const step = root.querySelector("[data-learn-step]")
  if (step) step.textContent = `${Math.min(state.index + 1, EMAILS.length)} of ${EMAILS.length}`
}

function finish(root, state) {
  const stage = root.querySelector("[data-learn-stage]")
  const choices = root.querySelector("[data-learn-choices]")
  const step = root.querySelector("[data-learn-step]")
  const end = root.querySelector("[data-learn-end]")
  if (stage) stage.hidden = true
  if (choices) choices.hidden = true
  if (step) step.hidden = true
  if (end) end.hidden = false
  const score = root.querySelector("[data-learn-score]")
  if (score) score.textContent = `${state.score} of ${EMAILS.length}`
  const verdict = root.querySelector("[data-learn-verdict]")
  if (verdict) verdict.textContent = verdictFor(state.score, EMAILS.length)
}

function reset(root) {
  writeState(root, {index: 0, score: 0, counts: {transactional: 0, notification: 0, bulk: 0}})
  const why = root.querySelector("[data-learn-why]")
  if (why) {
    why.textContent = ""
    why.classList.remove("text-good", "text-bad")
    why.classList.add("text-ink-muted")
  }
  const stage = root.querySelector("[data-learn-stage]")
  const choices = root.querySelector("[data-learn-choices]")
  const step = root.querySelector("[data-learn-step]")
  const end = root.querySelector("[data-learn-end]")
  if (stage) stage.hidden = false
  if (choices) choices.hidden = false
  if (step) step.hidden = false
  if (end) end.hidden = true
  paintCard(root, EMAILS[0], false)
  setBusy(root, false)
}

function answer(root, bucket) {
  const state = stateOf(root)
  const email = EMAILS[state.index]
  if (!email || root.dataset.learnBusy === "true") return
  root.dataset.learnBusy = "true"
  setBusy(root, true)
  const correct = email.answer === bucket
  if (correct) state.score += 1
  state.counts[bucket] += 1
  const why = root.querySelector("[data-learn-why]")
  if (why) {
    why.classList.remove("text-ink-muted", "text-good", "text-bad")
    why.classList.add(correct ? "text-good" : "text-bad")
    why.textContent = `${correct ? "Right." : "Wrong."} ${email.why}`
  }
  writeState(root, state)
  exitCard(root.querySelector("[data-learn-card]"), bucket, () => {
    state.index += 1
    writeState(root, state)
    root.dataset.learnBusy = "false"
    if (state.index >= EMAILS.length) {
      finish(root, state)
      return
    }
    paintCard(root, EMAILS[state.index], true)
    setBusy(root, false)
  })
}

export function mount(root) {
  writeState(root, {index: 0, score: 0, counts: {transactional: 0, notification: 0, bulk: 0}})
  root.querySelectorAll("[data-learn-sort]").forEach((button) => {
    button.addEventListener("click", () => answer(root, button.dataset.learnSort))
  })
  root.querySelector("[data-learn-retry]")?.addEventListener("click", () => reset(root))
}
